/**
 * Remember which baseline (and config, and colour) the user is displaying in each baseline slot.
 *
 * Mirrors performanceTargetPersistence: sessionStorage for the current tab, persistent settings for
 * across sessions. Session wins on restore because it reflects the most recent deliberate choice.
 *
 * "No baseline" is a real, sticky choice. It is stored as an explicit empty record rather than by
 * clearing the keys, so a user who turned the baseline off does not get one auto-selected again on
 * the next visit.
 *
 * There are two slots so two baselines can be compared at once. Slot 1 keeps the original,
 * unsuffixed keys — a user who saved a selection before the second slot existed still gets it back.
 */

import { persistentSettingsService } from '../services/persistentSettingsService';
import { cleanQuotes } from './global';
import { debug as logDebug } from './console';
import { normalizePerformanceTargetColor } from './performanceTargetColor';

/** Which of the two baseline overlays a call refers to. */
export type BaselineSlot = 1 | 2;

const SESSION_STORAGE_KEY = 'performanceBaseline';

/** Slot 1 keeps the legacy unsuffixed keys; slot 2 is suffixed. */
function slotSuffix(slot: BaselineSlot): string {
  return slot === 1 ? '' : String(slot);
}

function sessionKey(slot: BaselineSlot): string {
  return `${SESSION_STORAGE_KEY}${slotSuffix(slot)}`;
}

/** Spelled out rather than built from the suffix, so the keys stay literal types. */
function settingKeys(slot: BaselineSlot) {
  return slot === 1
    ? {
      id: 'performanceBaselineId',
      name: 'performanceBaselineName',
      config: 'performanceBaselineConfig',
      color: 'performanceBaselineColor',
    } as const
    : {
      id: 'performanceBaseline2Id',
      name: 'performanceBaseline2Name',
      config: 'performanceBaseline2Config',
      color: 'performanceBaseline2Color',
    } as const;
}

export interface PerformanceBaselineSelection {
  /** null means an explicit "No baseline". */
  baselineId: number | null;
  baselineName: string;
  baselineConfig: string;
  baselineColor?: string;
}

export function readPerformanceBaselineSession(
  slot: BaselineSlot = 1
): Partial<PerformanceBaselineSelection> | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(slot));
    if (!raw) { return null; }
    const parsed = JSON.parse(raw) as Partial<PerformanceBaselineSelection>;
    const id = Number(parsed.baselineId);
    return {
      baselineId: Number.isInteger(id) && id > 0 ? id : null,
      baselineName: typeof parsed.baselineName === 'string' ? parsed.baselineName : '',
      baselineConfig: typeof parsed.baselineConfig === 'string' ? parsed.baselineConfig : '',
      baselineColor: typeof parsed.baselineColor === 'string'
        ? normalizePerformanceTargetColor(parsed.baselineColor)
        : undefined,
    };
  } catch {
    return null;
  }
}

export function writePerformanceBaselineSession(
  partial: Partial<PerformanceBaselineSelection>,
  slot: BaselineSlot = 1
): void {
  try {
    const existing = readPerformanceBaselineSession(slot) || {};
    const next = { ...existing, ...partial };
    if (partial.baselineColor !== undefined) {
      next.baselineColor = normalizePerformanceTargetColor(partial.baselineColor);
    }
    sessionStorage.setItem(sessionKey(slot), JSON.stringify(next));
  } catch (error) {
    logDebug('performanceBaselinePersistence: Error writing sessionStorage', error);
  }
}

/**
 * The saved selection, or null when this user has never chosen on this project.
 *
 * The null-vs-explicit-none distinction matters: null means "no opinion", which lets the caller
 * decide whether to auto-select; a record with `baselineId: null` means "the user turned it off".
 */
export async function loadPerformanceBaselineFromPersistentSettings(
  userId: string,
  className: string,
  projectId: number,
  slot: BaselineSlot = 1
): Promise<PerformanceBaselineSelection | null> {
  try {
    const settings = await persistentSettingsService.loadSettings(userId, className, projectId);
    if (!settings) { return null; }

    const keys = settingKeys(slot);
    const hasKeys = keys.id in settings || keys.name in settings || keys.config in settings;
    if (!hasKeys) { return null; }

    const rawId = cleanQuotes(String(settings[keys.id] ?? '')).trim();
    const id = Number(rawId);
    return {
      baselineId: rawId !== '' && Number.isInteger(id) && id > 0 ? id : null,
      baselineName: cleanQuotes(String(settings[keys.name] ?? '')).trim(),
      baselineConfig: cleanQuotes(String(settings[keys.config] ?? '')).trim(),
      baselineColor: settings[keys.color]
        ? normalizePerformanceTargetColor(cleanQuotes(String(settings[keys.color])))
        : undefined,
    };
  } catch (error) {
    logDebug('performanceBaselinePersistence: Error loading from persistent settings', error);
    return null;
  }
}

export async function savePerformanceBaselineToPersistentSettings(
  userId: string,
  className: string,
  projectId: number,
  partial: Partial<PerformanceBaselineSelection>,
  slot: BaselineSlot = 1
): Promise<void> {
  const keys = settingKeys(slot);
  const payload: Record<string, string> = {};
  if (partial.baselineId !== undefined) {
    payload[keys.id] = partial.baselineId == null ? '' : String(partial.baselineId);
  }
  if (partial.baselineName !== undefined) { payload[keys.name] = partial.baselineName; }
  if (partial.baselineConfig !== undefined) { payload[keys.config] = partial.baselineConfig; }
  if (partial.baselineColor !== undefined) {
    payload[keys.color] = normalizePerformanceTargetColor(partial.baselineColor);
  }
  if (Object.keys(payload).length === 0) { return; }

  await persistentSettingsService.saveSettings(userId, className, projectId, payload);
  logDebug('performanceBaselinePersistence: Saved baseline settings', payload);
}
